import produce, { Draft } from "immer";
import Debug from "debug";

import {
  ChannelStore,
  ActionChannelGetAllSuccess,
  Stream,
  ActionChannelDeleteOneSuccess,
  PayloadChannelDeleteOne,
  ActionChannelCreateOneSuccess,
  ActionEditorAuthGetSuccess,
  PayloadEditorAuthGetSuccess,
  AuthMode,
} from "../types";
import { ACTIONS } from "../actions/constants";

const debug = Debug("broadcast:reducers:channels");

const processChannels = (
  channels: ChannelStore,
  draft: Draft<ChannelStore>,
  payload: Stream[]
): void => {
  const availableChannels = payload.filter(
    (channel) => !channel.channelDeleted
  );
  draft.available = availableChannels;
};

const processChannelDeletion = (
  channels: ChannelStore,
  draft: Draft<ChannelStore>,
  payload: PayloadChannelDeleteOne
): void => {
  debug("processChannelDeletion", payload);
  const { channelId } = payload;

  draft.available = draft.available.filter(
    (channel) => channel.channelId !== channelId
  );
};

const processChannelCreate = (
  channels: ChannelStore,
  draft: Draft<ChannelStore>,
  payload: Stream
): void => {
  debug("processChannelCreate", payload);

  draft.available.push(payload);
};

const processChannelEdit = (
  channels: ChannelStore,
  draft: Draft<ChannelStore>,
  payload: Stream
): void => {
  debug("processChannelEdit", payload);

  const { channelId } = payload;

  draft.available = draft.available.map((channel) =>
    channel.channelId === channelId ? payload : channel
  );
};

const processEditorAuth = (
  channels: ChannelStore,
  draft: Draft<ChannelStore>,
  payload: PayloadEditorAuthGetSuccess
): void => {
  debug("processEditorAuth", payload);

  const { user, arn } = payload;
  const { team, authProvider } = user;
  const { id: teamId, name: teamName } = team;

  const channel = draft.available.find((chan) => chan.arn === arn);
  if (channel) {
    channel.orgId = teamId;
    channel.orgName = teamName;
    channel.password = "";
    channel.hasPassword = false;
    channel.authMode =
      authProvider.toString() === AuthMode.Slack.toString()
        ? AuthMode.Slack
        : AuthMode.Teams;

    draft.available = draft.available.map((chan) =>
      chan.channelId === channel.channelId ? channel : chan
    );
  }
};

export const channelsReducer = (
  channels = { available: [] as Stream[] },
  {
    type,
    payload,
  }:
    | ActionChannelGetAllSuccess
    | ActionChannelDeleteOneSuccess
    | ActionChannelCreateOneSuccess
    | ActionEditorAuthGetSuccess
): ChannelStore =>
  produce(channels, (draft) => {
    switch (type) {
      case ACTIONS.CHANNELS_GET_ALL_REQUEST:
        break;
      case ACTIONS.CHANNELS_GET_ALL_FAILURE:
        break;
      case ACTIONS.CHANNELS_GET_ALL_SUCCESS: {
        processChannels(channels, draft, payload as Stream[]);
        break;
      }
      case ACTIONS.CHANNELS_DELETE_ONE_REQUEST:
        break;
      case ACTIONS.CHANNELS_DELETE_ONE_FAILURE:
        break;
      case ACTIONS.CHANNELS_DELETE_ONE_SUCCESS: {
        processChannelDeletion(
          channels,
          draft,
          payload as PayloadChannelDeleteOne
        );
        break;
      }
      case ACTIONS.CHANNELS_CREATE_ONE_REQUEST:
        break;
      case ACTIONS.CHANNELS_CREATE_ONE_FAILURE:
        break;
      case ACTIONS.CHANNELS_CREATE_ONE_SUCCESS: {
        processChannelCreate(channels, draft, payload as Stream);
        break;
      }
      case ACTIONS.CHANNELS_EDIT_ONE_REQUEST:
        break;
      case ACTIONS.CHANNELS_EDIT_ONE_FAILURE:
        break;
      case ACTIONS.CHANNELS_EDIT_ONE_SUCCESS:
      case ACTIONS.EDITOR_AUTH_REMOVE_SUCCESS:
      case ACTIONS.EDITOR_PASSWORD_AUTH_SUCCESS:
      case ACTIONS.EDITOR_PUBLIC_PRIVATE_AUTH_SUCCESS: {
        processChannelEdit(channels, draft, payload as Stream);
        break;
      }
      case ACTIONS.EDITOR_AUTH_SUCCESS: {
        processEditorAuth(
          channels,
          draft,
          payload as PayloadEditorAuthGetSuccess
        );
        break;
      }

      default:
        break;
    }
  });
